import type { CreditSimulatorState } from './useCreditSimulator';
import type {
	CreateCreditLeadParam,
	LeadsRepository
} from '~~/core/ports/repositories';

import { useAdCampaign } from '~/composables/adCampaign';
import { useLogger } from '~/composables/useLogger';
import { nuxtLeadsRepository } from '~~/adapters/repositories/nuxt/nuxtLeadsRepository';
import { getStatusCode } from '~~/core/helpers/statusCode';

import {
	calculateConsortium,
	calculateFinancing,
	calculateHomeEquity
} from './creditCalculations';
import { getCpfDigits } from './creditFormat';
import { useCreditSimulator } from './useCreditSimulator';

export interface CreditContact {
	name?: string;
	phone: string;
	email?: string;
}

type CreateCreditLead = LeadsRepository['createCreditLead'];

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
// Heimdall answers 422 to any other key, which would lose the lead.
const CAMPAIGN_KEYS = [
	'adCampaign',
	'adCampaignId',
	'adSet',
	'adSetId',
	'adId',
	'adName',
	'adPhId'
];

function roundValues(values: Record<string, number>): Record<string, number> {
	return Object.fromEntries(
		Object.entries(values).map(([key, value]) => [
			key,
			Math.round(value * 100) / 100
		])
	);
}

function getSimulation(state: CreditSimulatorState): {
	simulation: Record<string, number>;
	estimate: Record<string, number>;
} {
	const { inputs } = state;

	if (state.product === 'financing') {
		const propertyValue = inputs.financingValue;
		const downPaymentPercent = inputs.financingDownPaymentPercent;
		const years = inputs.financingYears;
		const { downPayment: _, ...estimate } = calculateFinancing({
			propertyValue,
			downPaymentPercent,
			years
		});
		return {
			simulation: { propertyValue, downPaymentPercent, years },
			estimate
		};
	}

	if (state.product === 'consortium') {
		const letterValue = inputs.consortiumLetter;
		const months = inputs.consortiumMonths;
		return {
			simulation: { letterValue, months },
			estimate: { ...calculateConsortium({ letterValue, months }) }
		};
	}

	const propertyValue = inputs.homeEquityValue;
	const creditPercent = inputs.homeEquityPercent;
	const months = inputs.homeEquityMonths;
	return {
		simulation: { propertyValue, creditPercent, months },
		estimate: {
			...calculateHomeEquity({ propertyValue, creditPercent, months })
		}
	};
}

export function buildCreditLead(
	state: CreditSimulatorState,
	contact: CreditContact,
	campaign: Record<string, string>,
	formHash: string
): CreateCreditLeadParam {
	const { simulation, estimate } = getSimulation(state);

	return {
		product: state.product,
		simulation: roundValues(simulation),
		estimate: roundValues(estimate),
		campaign: Object.fromEntries(
			CAMPAIGN_KEYS.filter(key => campaign[key]).map(key => [
				key,
				campaign[key]!.slice(0, 200)
			])
		),
		intent: state.intent,
		income: state.income,
		name: contact.name?.trim() || null,
		phone: contact.phone,
		cpf: getCpfDigits(state.cpf) || null,
		email: contact.email || null,
		privacyAccepted: true,
		formHash
	};
}

// The form_hash makes a retry safe: Heimdall drops a lead it already sent.
export async function sendCreditLead(
	params: CreateCreditLeadParam,
	create: CreateCreditLead = nuxtLeadsRepository.createCreditLead,
	wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
): Promise<boolean> {
	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		const [, error] = await create(params);
		if (!error) return true;

		const statusCode = getStatusCode(error.originalErrorObject);
		// A 4xx answer does not change on retry.
		if (statusCode && statusCode < 500) return false;
		if (attempt < MAX_ATTEMPTS) await wait(RETRY_DELAY_MS * attempt);
	}
	return false;
}

export function useCreditLeadSubmit() {
	const simulator = useCreditSimulator();
	const { getAdCampaign } = useAdCampaign();
	const logger = useLogger('CreditLeadSubmit');
	const isSending = ref(false);
	const hasSendError = ref(false);

	async function send(contact: CreditContact): Promise<boolean> {
		isSending.value = true;
		hasSendError.value = false;
		try {
			const lead = buildCreditLead(
				simulator.state.value,
				contact,
				getAdCampaign(),
				simulator.ensureFormHash()
			);
			const sent = await sendCreditLead(lead);
			// A network failure never reaches the server log; no personal data here.
			if (!sent) {
				logger.error('Lead de crédito não enviado após as tentativas', {
					product: lead.product,
					formHash: lead.formHash
				});
			}
			hasSendError.value = !sent;
			return sent;
		} finally {
			isSending.value = false;
		}
	}

	return { isSending, hasSendError, send };
}
